import { useEffect } from "react";
import {
  Banknote,
  Coins,
  Crown,
  HandCoins,
  Landmark,
  ShieldCheck,
  Sparkles,
  Users,
  WalletCards,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { getWalletBalance } from "../../../Redux/wallet_apis/slice";
import "./UserSalarPayPage.css";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);

type WalletItemKind = "cash" | "reward" | "fund";

interface WalletItem {
  id: string;
  code: string;
  name: string;
  value: number | string;
  meta: string;
  kind: WalletItemKind;
}

const getWalletIcon = (kind: WalletItemKind) => {
  if (kind === "reward") return <Coins size={18} strokeWidth={2} />;
  if (kind === "fund") return <Landmark size={18} strokeWidth={2} />;
  return <HandCoins size={18} strokeWidth={2} />;
};

const SalarPay = () => {
  const dispatch = useAppDispatch();

  const { balance, loading, error } = useAppSelector((state) => state.wallet);
  const { user } = useAppSelector((state) => state.customerAuth);

  useEffect(() => {
    if (user?._id) {
      dispatch(getWalletBalance());
    }
  }, [dispatch, user?._id]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  const findCompBalance = (compName: string, oldVal?: number) => {
    if (balance?.components) {
      return (
        balance.components.find((c) => c.component === compName)?.balance ?? 0
      );
    }
    return oldVal ?? 0;
  };

  const walletItems: WalletItem[] = [
    {
      id: "referral",
      code: "R",
      name: "Referral Incentives",
      value: findCompBalance(
        "REFERRAL_COMMISSION",
        balance?.commissionBalances?.referralCommission,
      ),
      meta: "Referral earnings",
      kind: "cash",
    },
    {
      id: "flow",
      code: "SF",
      name: "Salar Flow Incentives",
      value: findCompBalance(
        "FLOW_COMMISSION",
        balance?.commissionBalances?.flowCommission,
      ),
      meta: "Flow network earnings",
      kind: "cash",
    },
    {
      id: "samriddhi",
      code: "SS",
      name: "Salar Samriddhi Incentive",
      value: findCompBalance(
        "SAMRIDDHI_COMMISSION",
        balance?.commissionBalances?.samriddhiCommission,
      ),
      meta: "Samriddhi earnings",
      kind: "cash",
    },
    {
      id: "milestone",
      code: "SM",
      name: "Salar Milestone Incentive",
      value: findCompBalance(
        "MILESTONE_REFERRAL_COMMISSION",
        balance?.commissionBalances?.milestoneReferralCommission,
      ),
      meta: "Milestone earnings",
      kind: "cash",
    },
    {
      id: "loyalty",
      code: "LC",
      name: "Loyalty Referral Incentive",
      value: "comming soon",
      meta: "Loyalty earnings",
      kind: "cash",
    },
    {
      id: "community",
      code: "CG",
      name: "Community Growth Incentive",
      value: findCompBalance(
        "COMMUNITY_GROWTH_COMMISSION",
        balance?.commissionBalances?.communityGrowthCommission,
      ),
      meta: "Community earnings",
      kind: "cash",
    },
    {
      id: "partner",
      code: "SP",
      name: "Salar Partner Incentive",
      value: findCompBalance(
        "PARTNER_GROWTH_REFERRAL",
        balance?.commissionBalances?.partnerGrowthReferral,
      ),
      meta: "Partner growth earnings",
      kind: "cash",
    },
    {
      id: "pearls",
      code: "P",
      name: "Salar Pearls Incentive",
      value: findCompBalance(
        "PEARLS_COMMISSION",
        balance?.commissionBalances?.pearlsCommission,
      ),
      meta: "Pearls reward earnings",
      kind: "reward",
    },
    {
      id: "unity",
      code: "SU",
      name: "Salar Unity Incentive",
      value: findCompBalance(
        "UNITY_COMMISSION",
        balance?.commissionBalances?.unityCommission,
      ),
      meta: "Unity earnings",
      kind: "cash",
    },
    {
      id: "funds",
      code: "F",
      name: "Funds",
      value: findCompBalance("FUNDS", balance?.funds),
      meta: "Wallet funds",
      kind: "fund",
    },
  ];

  const pearlsItem = walletItems.find((i) => i.id === "pearls");
  const pearlsValue =
    typeof pearlsItem?.value === "number" ? pearlsItem.value : 0;

  return (
    <section className="spv3-page">
      <div className="spv3-shell">
        <header className="spv3-header">
          <div className="spv3-header-left">
            <div className="spv3-title-mark">
              <WalletCards size={22} strokeWidth={2} />
            </div>
            <div className="spv3-heading-copy">
              <span className="spv3-eyebrow">MY WALLET</span>
              <h1>Salar Pay</h1>
              <p>
                Balance, rewards and incentive earnings in one clean wallet
                view.
              </p>
            </div>
          </div>
          <div className="spv3-security">
            <ShieldCheck size={15} strokeWidth={2} />
            Protected
          </div>
        </header>

        {error && (
          <div className="alert alert-danger text-white mt-3 py-2 fs-7" role="alert">
            {error}
          </div>
        )}

        <section className="spv3-balance">
          <div className="spv3-balance-main">
            <div className="spv3-balance-topline">
              <div className="spv3-balance-icon">
                <Banknote size={24} strokeWidth={1.9} />
              </div>
              <div>
                <span>AVAILABLE BALANCE</span>
                <p>Salar Pay wallet</p>
              </div>
            </div>
            <strong className="spv3-balance-value">
              {formatCurrency(
                (balance?.totalAvailable ?? balance?.availableBalance ?? 0) as number,
              )}
            </strong>
            <div className="spv3-balance-caption">
              <Sparkles size={14} strokeWidth={2} />
              Your consolidated available cash balance
            </div>
          </div>

          <div className="spv3-balance-side">
            <div className="spv3-side-stat">
              <span className="spv3-side-icon">
                <Users size={17} strokeWidth={2} />
              </span>
              <div>
                <small>Earning Sources</small>
                <strong>{walletItems.length}</strong>
              </div>
            </div>
            <div className="spv3-side-stat">
              <span className="spv3-side-icon reward">
                <Crown size={17} strokeWidth={2} />
              </span>
              <div>
                <small>Salar Pearls</small>
                <strong>{formatCurrency(pearlsValue)}</strong>
              </div>
            </div>
            <div className="spv3-side-stat">
              <span className="spv3-side-icon">
                <ShieldCheck size={17} strokeWidth={2} />
              </span>
              <div>
                <small>Wallet Status</small>
                <strong>Active</strong>
              </div>
            </div>
          </div>
        </section>

        <section className="spv3-ledger">
          <div className="spv3-ledger-head">
            <div>
              <span>WALLET LEDGER</span>
              <h2>Incentive balances</h2>
              <p>A compact breakdown of every earning source.</p>
            </div>
            <div className="spv3-ledger-total">
              {walletItems.length} balances
            </div>
          </div>

          <div className="spv3-ledger-grid">
            {walletItems.map((item) => (
              <article
                key={item.id}
                className={`spv3-wallet-row ${
                  item.kind === "reward" ? "is-reward" : ""
                }`}
              >
                <div className="spv3-row-leading">
                  <div className="spv3-code-box">{item.code}</div>
                  <div className="spv3-row-copy">
                    <span className="spv3-row-icon">
                      {getWalletIcon(item.kind)}
                    </span>
                    <div>
                      <h3>{item.name}</h3>
                      <p>{item.meta}</p>
                    </div>
                  </div>
                </div>
                <div className="spv3-row-value">
                  <strong>
                    {typeof item.value === "number"
                      ? formatCurrency(item.value)
                      : item.value}
                  </strong>
                  <span>
                    {item.kind === "reward" ? "Points" : "Available"}
                  </span>
                </div>
              </article>
            ))}
          </div>
        </section>

        <footer className="spv3-note">
          <Coins size={16} strokeWidth={2} />
          Salar Pearls are reward points and are displayed separately from cash
          incentive balances.
        </footer>
      </div>
    </section>
  );
};

export default SalarPay;
